import type { CSSProperties } from 'react'
import type { DatePickerProps } from 'antd'
import type { RangePickerProps } from 'antd/es/date-picker'
import { useState, useEffect, useMemo } from 'react'
import { TimePicker, DatePicker as AntdDataPicker } from 'antd'
import { $consts } from 'src/plugins'
import { usePrevious, useLatest } from 'src/hooks'
import { getFormatMoment } from 'src/components/common/helper'
import dayjs from 'dayjs'

const { RangePicker } = AntdDataPicker

const getPickerType = (type?: string): 'timePicker' | 'datePicker' | 'rangePicker' => {
  switch (type) {
    case $consts['COMMON/DATE_TYPE_SECOND']:
      return 'timePicker'
    case $consts['COMMON/DATE_TYPE_RANGE_DAY_SECOND']:
    case $consts['COMMON/DATE_TYPE_RANGE_DAY_MINUTE']:
    case $consts['COMMON/DATE_TYPE_RANGE_DAY']:
    case $consts['COMMON/DATE_TYPE_RANGE_WEEK']:
    case $consts['COMMON/DATE_TYPE_RANGE_MONTH']:
    case $consts['COMMON/DATE_TYPE_RANGE_QUARTER']:
    case $consts['COMMON/DATE_TYPE_RANGE_YEAR']:
      return 'rangePicker'
    default:
      return 'datePicker'
  }
}

const getPicker = (type?: string): 'date' | 'week' | 'month' | 'quarter' | 'year' | undefined => {
  switch (type) {
    case $consts['COMMON/DATE_TYPE_DAY_SECOND']:
    case $consts['COMMON/DATE_TYPE_DAY']:
    case $consts['COMMON/DATE_TYPE_RANGE_DAY']:
      return 'date'
    case $consts['COMMON/DATE_TYPE_WEEK']:
    case $consts['COMMON/DATE_TYPE_RANGE_WEEK']:
      return 'week'
    case $consts['COMMON/DATE_TYPE_MONTH']:
    case $consts['COMMON/DATE_TYPE_RANGE_MONTH']:
      return 'month'
    case $consts['COMMON/DATE_TYPE_QUARTER']:
    case $consts['COMMON/DATE_TYPE_RANGE_QUARTER']:
      return 'quarter'
    case $consts['COMMON/DATE_TYPE_YEAR']:
    case $consts['COMMON/DATE_TYPE_RANGE_YEAR']:
      return 'year'
    default:
      return undefined
  }
}

type Props = {
  datePicker?: DatePickerProps,
  rangePicker?: RangePickerProps
  disabled?: boolean,
  value?: string,
  format?: string,
  dateType?: string,
  onChange?: (date: string) => void,
  className?: string,
  style?: CSSProperties
}

const DatePicker = (props: Props) => {
  const pickerType = useMemo(() => getPickerType(props.dateType), [props.dateType])

  const picker = useMemo(() => getPicker(props.dateType), [props.dateType])

  const format = useMemo(() => {
    switch (props.dateType) {
      case $consts['COMMON/DATE_TYPE_RANGE_DAY_MINUTE']:
        return 'YYYY-MM-DD HH:mm'
      default:
        return undefined
    }
  }, [props.dateType])

  const showTime = useMemo(() => {
    switch (props.dateType) {
      case $consts['COMMON/DATE_TYPE_DAY_SECOND']:
      case $consts['COMMON/DATE_TYPE_RANGE_DAY_SECOND']:
        return true
      case $consts['COMMON/DATE_TYPE_RANGE_DAY_MINUTE']:
        return { format: 'HH:mm' }
      default:
        return false
    }
  }, [props.dateType])

  const [value, setValue] = useState<any>([])
  useEffect(() => {
    if (props.value) {
      const arr = props.value.split(',')
      setValue(arr.map((date: string) => {
        // 兼容只有 18:00:00 的情况
        if (pickerType === 'timePicker' && dayjs(date, 'HH:mm:ss', true).isValid()) {
          return dayjs(date, 'HH:mm:ss', true)
        }
        return dayjs(date)
      }))
    } else {
      setValue([])
    }
  }, [props.value, pickerType])

  const handleDateChange = (date: any) => {
    props.onChange?.(getFormatMoment(date, { dateType: props.dateType, targetFormat: props.format }))
  }

  const handleRangeChange = (dates: any) => {
    props.onChange?.(dates?.map((date: any, index: number, arr: any[]) => {
      return getFormatMoment(date, { dateType: props.dateType, isEndDate: index === arr.length - 1, targetFormat: props.format })
    }).join(','))
  }

  const prevDateType = usePrevious(props.dateType)
  const latestValue = useLatest(value)
  useEffect(() => {
    if (prevDateType && props.dateType !== prevDateType) {
      if (getPickerType(props.dateType) === 'datePicker') {
        props.onChange?.(getFormatMoment(latestValue.current[0], { dateType: props.dateType, targetFormat: props.format }))
      } else {
        props.onChange?.(latestValue.current.map((date: any, index: number, arr: any[]) => {
          return getFormatMoment(date, { dateType: props.dateType, isEndDate: index === arr.length - 1, targetFormat: props.format })
        }).join(','))
      }
    }
  }, [props.dateType])

  switch (pickerType) {
    case 'timePicker':
      return (
        <TimePicker
          allowClear
          disabled={props.disabled}
          value={value[0]}
          onChange={handleDateChange}
          className={props.className}
          style={props.style}
        />
      )
    case 'datePicker':
      return (
        <AntdDataPicker
          allowClear
          {...props.datePicker as any}
          disabled={props.disabled}
          picker={picker}
          showTime={showTime}
          value={value[0]}
          onChange={handleDateChange}
          className={props.className}
          style={props.style}
        />
      )
    default:
      return (
        <RangePicker
          allowClear
          {...props.rangePicker}
          disabled={props.disabled}
          picker={picker}
          format={format}
          showTime={showTime}
          value={value}
          onChange={handleRangeChange}
          className={props.className}
          style={props.style}
        />
      )
  }
}

export default DatePicker